import React, { useCallback, useEffect, useMemo, useState } from "react";
import { subMonths } from "date-fns";
import MonthPicker from "../../../../../components/form/month-picker";
import { Select } from "../../../../../components/form/select";
import ButtonIcon from "../../../../../components/ButtonIcon/ButtonIcon";
import CaretRight from "../../../../../assets/svg/icons/common/CaretRight";
import CloseIcon24 from "../../../../../assets/svg/icons/common/CloseIcon24";
import useCommonStore from "../../../../../stores/common";
import useResidualStore from "../../../../../stores/residuals";
import HeadingWithLine from "../../../../../components/HeadingWithLine/HeadingWithLine";
import { Copier } from "../../../../../../components/copier";
import CopyIcon from "../../../../../assets/svg/icons/common/copy";
import MobileDesktopAlert from "../../../../../components/MobileDesktopAlert";
import Table from "../../../../../components/Table";
import { useMediaQuery } from "../../../../../hooks/useMediaQuery";
import PaymentAuthorizationReportSkeleton from "../components/PaymentAuthorizationReportSkeleton";
import MerchantRefundsTable from "../components/MerchantRefundsTable";
import {
  formatDisplayDate,
  formatMoney,
  monthAbbrevAndYearToCalendarMonth,
} from "../formatters";
import styles from "../styles.module.scss";
import ledger from "../invoice-statement/ledgerTable.module.scss";

const cell = (value) => (value != null && value !== "" ? String(value) : "—");

const MobilePaymentLineCard = ({ line, showDividerBelow }) => (
  <>
    <div
      className={`${styles.dataContainer} ${styles.dataContainerLine} ${styles.lineItemRow}`}
    >
      <div className={styles.dataItem}>
        <span className={styles.lineItemFieldLabel}>
          Payment Line Description
        </span>
        <div>
          <span>{line.authorization_description || "—"}</span>
        </div>
      </div>
      <div className={styles.dataItem}>
        <span className={styles.lineItemFieldLabel}>Account Description</span>
        <div>
          <span>{line.payment_description || "—"}</span>
        </div>
      </div>
      <div className={styles.dataItem}>
        <span className={styles.lineItemFieldLabel}>Payment Amount</span>
        <div>
          <span>{formatMoney(line.amount)}</span>
        </div>
      </div>
    </div>
    {line?.refunds?.length > 0 ? <MerchantRefundsTable line={line} /> : null}
    {showDividerBelow ? <hr className={styles.lineItemDivider} /> : null}
  </>
);

const PaymentAuthorization = () => {
  const isPaymentAuthNarrowLayout = useMediaQuery(1024);
  const setShowDetails = useCommonStore((state) => state.setShowDetails);
  const setExpandDetails = useCommonStore((state) => state.setExpandDetails);
  const officeOptions = useResidualStore((s) => s.paymentAuthOfficeOptions);
  const selectedOffice = useResidualStore((s) => s.paymentAuthSelectedOffice);
  const officesLoading = useResidualStore((s) => s.paymentAuthOfficesLoading);
  const setPaymentAuthSelectedOffice = useResidualStore(
    (s) => s.setPaymentAuthSelectedOffice,
  );
  const setPaymentAuthAppliedPeriod = useResidualStore(
    (s) => s.setPaymentAuthAppliedPeriod,
  );
  const reportData = useResidualStore((s) => s.paymentAuthReportData);
  const reportLoading = useResidualStore((s) => s.paymentAuthReportLoading);
  const authorizations = useMemo(
    () =>
      Array.isArray(reportData?.authorizations)
        ? reportData.authorizations
        : [],
    [reportData?.authorizations],
  );

  const [expandedLineIds, setExpandedLineIds] = useState(() => new Set());

  useEffect(() => {
    setExpandedLineIds(new Set());
  }, [selectedOffice, authorizations.length]);

  const toggleLineRow = useCallback((id) => {
    setExpandedLineIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const renderRefundNestedTable = useCallback((row) => {
    const refunds = row.refunds ?? [];
    if (!refunds.length) {
      return null;
    }
    const last = refunds.length - 1;
    return (
      <div className={ledger.nestedContainer}>
        <table className={ledger.nestedTable}>
          <thead>
            <tr className={ledger.nestedHeaderRow}>
              <td></td>
              <td>MID</td>
              <td>DBA</td>
              <td>TRANSACTION AMOUNT</td>
            </tr>
          </thead>
          <tbody>
            {refunds.map((r, index) => (
              <tr
                key={r.integer_key ?? `${row.id}-${index}`}
                className={`${ledger.nestedRow} ${ledger.nestedChildApp} ${index !== last ? ledger.nestedLine : ""}`}
              >
                <td>
                  <div className={ledger.normalCell}>{cell(r.mid)}</div>
                </td>
                <td title={cell(r.dba_name)}>
                  <div className={ledger.normalCell}>{cell(r.dba_name)}</div>
                </td>
                <td
                  className={ledger.nestedAmountCell}
                  title={formatMoney(r.transaction_amount)}
                >
                  {formatMoney(r.transaction_amount)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }, []);

  const lineItemColumns = useMemo(
    () => [
      {
        field: "expand",
        width: "16px",
        headerName: "",
        renderCell: (_, row) => {
          const has = (row.refunds?.length ?? 0) > 0;
          if (!has) {
            return null;
          }
          const isOpen = expandedLineIds.has(row.id);
          return (
            <button
              type="button"
              className={`${ledger.expandButton} ${isOpen ? ledger.expandCaretOpen : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                toggleLineRow(row.id);
              }}
              aria-expanded={isOpen}
              aria-label={isOpen ? "Collapse refunds" : "Expand refunds"}
            >
              <CaretRight />
            </button>
          );
        },
      },
      {
        field: "authorization_description",
        headerName: "PAYMENT LINE DESCRIPTION",
        width: "220px",
        overflow: true,
        renderCell: (_, row) => (
          <div
            className={ledger.normalCell}
            title={cell(row.authorization_description)}
          >
            {cell(row.authorization_description)}
          </div>
        ),
      },
      {
        field: "payment_description",
        headerName: "ACCOUNT DESCRIPTION",
        width: "220px",
        overflow: true,
        renderCell: (_, row) => (
          <div
            className={ledger.normalCell}
            title={cell(row.payment_description)}
          >
            {cell(row.payment_description)}
          </div>
        ),
      },
      {
        field: "amount",
        headerName: "PAYMENT AMOUNT",
        width: "140px",
        renderCell: (_, row) => (
          <div className={ledger.mainAmountCell}>{formatMoney(row.amount)}</div>
        ),
      },
    ],
    [expandedLineIds, toggleLineRow],
  );

  const onCloseClick = () => {
    setShowDetails(false);
    setExpandDetails(false);
  };

  const onMonthSubmit = ({ year, month: monthAbbrev }) => {
    const next = monthAbbrevAndYearToCalendarMonth(monthAbbrev, year);
    if (next) {
      setPaymentAuthAppliedPeriod(next);
    }
  };

  const hasReport = reportData && authorizations.length > 0;

  return (
    <div className={`${styles.container} ${styles.paymentAuthorizationRoot}`}>
      <header className={styles.header}>
        <div className={styles.closeContainer}>
          <ButtonIcon onClick={onCloseClick}>
            <CloseIcon24 />
          </ButtonIcon>
        </div>

        <div className={styles.titleContainer}>
          <h2 className={styles.title}>Payment Authorizations</h2>

          <div className={styles.buttonsContainer}>
            <MonthPicker
              variant="smallFilter"
              onSubmit={onMonthSubmit}
              changeOnSubmit
              showActions
              defaultDate={subMonths(new Date(), 1)}
            />

            <div className={styles.officeSelectWrap}>
              <Select
                hideClearButton
                name="office"
                placeholder={officesLoading ? "Loading" : "Select office"}
                options={officeOptions}
                value={selectedOffice}
                onChange={setPaymentAuthSelectedOffice}
                disabled={officesLoading}
                secondary
                customClass={styles.officeSelect}
              />
            </div>
          </div>
        </div>
      </header>
      <main>
        {reportLoading && <PaymentAuthorizationReportSkeleton />}
        {!reportLoading && !hasReport && selectedOffice && (
          <p className={styles.reportStatus}>
            No payment authorizations for this office and month/year.
          </p>
        )}
        {!reportLoading &&
          hasReport &&
          authorizations.map((auth, authIndex) => {
            const lineItems = Array.isArray(auth.line_items)
              ? auth.line_items
              : [];
            const tableRows = lineItems.map((line, idx) => ({
              ...line,
              id: String(line.id ?? `auth-${authIndex}-line-${idx}`),
              refunds: Array.isArray(line.refunds) ? line.refunds : [],
            }));
            return (
              <React.Fragment
                key={auth.authorization_number ?? `auth-${authIndex}`}
              >
                <HeadingWithLine
                  text={String(auth.payment_method ?? "").trim() || "—"}
                />

                <div className={styles.dataContainer}>
                  <div className={styles.dataItem}>
                    <span>Authorization Date</span>
                    <div>
                      <span>{formatDisplayDate(auth.process_date)}</span>
                    </div>
                  </div>
                  <div className={styles.dataItem}>
                    <span>Rep Name</span>
                    <div>
                      <span>{auth.rep_name || "—"}</span>
                    </div>
                  </div>
                  <div className={styles.dataItem}>
                    <span>Paid date</span>
                    <div>
                      <span>{formatDisplayDate(auth.date_posted)}</span>
                    </div>
                  </div>
                  <div className={styles.dataItem}>
                    <span>Office</span>
                    <div>
                      <span>{auth.office ?? "—"}</span>
                      {auth.office ? (
                        <Copier text={String(auth.office)}>
                          <CopyIcon />
                        </Copier>
                      ) : null}
                    </div>
                  </div>
                  <div className={styles.dataItem}>
                    <span>Authorization Number</span>
                    <div>
                      <span>{auth.authorization_number || "—"}</span>
                    </div>
                  </div>
                  <div className={styles.dataItem}>
                    <span>Agent</span>
                    <div>
                      <div className={styles.nameFLetter}>
                        {auth?.rep_name?.charAt(0).toUpperCase() || ""}
                      </div>
                      <p>{auth.rep_name || "—"}</p>
                    </div>
                  </div>
                  <div className={styles.dataItem}>
                    <span>Requested by</span>
                    <div>
                      <div className={styles.nameFLetter}>
                        {auth?.requested_by?.charAt(0).toUpperCase() || ""}
                      </div>
                      <p>{auth.requested_by || "—"}</p>
                    </div>
                  </div>
                  <div className={styles.dataItem}>
                    <span>Approved by</span>
                    <div>
                      <div className={styles.nameFLetter}>
                        {auth?.approved_by?.charAt(0).toUpperCase() || ""}
                      </div>
                      <p>{auth.approved_by || "—"}</p>
                    </div>
                  </div>
                </div>

                {lineItems.length > 0 && (
                  <div className={styles.lineItemsSection}>
                    <HeadingWithLine text="Line Items" />

                    {!isPaymentAuthNarrowLayout ? (
                      <>
                        <div className={ledger.tableContainer}>
                          <Table
                            key={`payment-auth-lines-${selectedOffice}-${auth.authorization_number ?? authIndex}`}
                            columns={lineItemColumns}
                            data={tableRows.map((row) => ({
                              ...row,
                              expanded: expandedLineIds.has(row.id),
                            }))}
                            renderExpandedContent={renderRefundNestedTable}
                            expandedContentClassName={
                              ledger.ledgerExpandedContentNoPad
                            }
                            dataLoading={false}
                            sortBy={null}
                            sortColumn={null}
                            className={styles.agentStatementLedgerTable}
                            disablePaddingOnExpandedRow
                            noMarginTop
                          />
                        </div>

                        <div
                          className={`${styles.dataContainer} ${styles.dataContainerLine} ${styles.lineItemsTotalRow}`}
                        >
                          <div className={styles.lineItemsTotalRowFirst}>
                            Total for:{" "}
                            {auth.authorization_number
                              ? String(auth.authorization_number)
                              : "—"}
                          </div>
                          <div
                            className={`${styles.lineItemCell} ${styles.lineItemTotalAmount}`}
                            aria-label={`Total amount, ${formatMoney(auth.total_amount)}`}
                          >
                            {formatMoney(auth.total_amount)}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className={styles.paymentAuthMobileLineItems}>
                        {lineItems.map((line, index) => (
                          <React.Fragment key={line.id ?? index}>
                            <MobilePaymentLineCard
                              line={line}
                              showDividerBelow={index < lineItems.length - 1}
                            />
                          </React.Fragment>
                        ))}

                        <div
                          className={`${styles.dataContainer} ${styles.dataContainerLine} ${styles.lineItemsTotalRow}`}
                        >
                          <div className={styles.lineItemsTotalRowFirst}>
                            Total for:{" "}
                            {auth.authorization_number
                              ? String(auth.authorization_number)
                              : "—"}
                          </div>
                          <div
                            className={`${styles.lineItemCell} ${styles.lineItemTotalAmount}`}
                            aria-label={`Total amount, ${formatMoney(auth.total_amount)}`}
                          >
                            {formatMoney(auth.total_amount)}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </React.Fragment>
            );
          })}
      </main>

      <MobileDesktopAlert message="See more details on the desktop version" />
    </div>
  );
};

export default PaymentAuthorization;
